import React, { useEffect, useState, useCallback } from "react";
import { Loader2, Plus, Edit3, Trash2, Save, X, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const inputCls =
  "w-full bg-white border border-slate-300 rounded-md px-3 py-2 text-sm text-slate-900 focus:border-spike-blue focus:ring-2 focus:ring-spike-blue/20 outline-none";
const labelCls = "block text-xs font-medium text-slate-700 mb-1.5";

const TimeSlotForm = ({ initial, venueId, onCancel, onSaved }) => {
  const [time, setTime] = useState(initial?.time || "10:00");
  const [price, setPrice] = useState(initial?.price_cad ?? 35);
  const [active, setActive] = useState(initial?.active ?? true);
  const [busy, setBusy] = useState(false);
  const isEdit = !!initial?.id;

  const save = async () => {
    if (!/^\d{2}:\d{2}$/.test(time)) {
      toast.error("Time must be HH:MM (24h)");
      return;
    }
    setBusy(true);
    try {
      const body = { time, price_cad: Number(price), active };
      if (isEdit) {
        await api.patch(`/admin/time-slots/${initial.id}`, body, { withCredentials: true });
        toast.success("Time slot updated.");
      } else {
        await api.post(`/admin/venues/${venueId}/time-slots`, body, { withCredentials: true });
        toast.success("Time slot created.");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-spike-blue/40 rounded-lg p-5 shadow-card-hover" data-testid="ts-form">
      <div className="font-semibold text-slate-900 mb-4">{isEdit ? `Edit slot · ${initial.time}` : "New time slot"}</div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className={labelCls}>Time (HH:MM, 24h)</label>
          <input data-testid="ts-input-time" className={inputCls} value={time} onChange={(e) => setTime(e.target.value)} placeholder="18:00" />
        </div>
        <div>
          <label className={labelCls}>Price (CAD)</label>
          <input type="number" min={0} step="0.01" data-testid="ts-input-price" className={inputCls} value={price} onChange={(e) => setPrice(e.target.value)} />
        </div>
        <div className="flex items-end">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" data-testid="ts-input-active" checked={active} onChange={(e) => setActive(e.target.checked)} className="w-4 h-4 accent-spike-blue" />
            <span className="text-sm text-slate-700">Active</span>
          </label>
        </div>
      </div>
      <div className="flex gap-2 mt-5">
        <button type="button" data-testid="ts-save" onClick={save} disabled={busy} className="inline-flex items-center gap-2 bg-spike-blue hover:bg-spike-blue-hover disabled:opacity-50 text-white text-sm font-medium px-4 py-2 rounded-md">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
        </button>
        <button type="button" data-testid="ts-cancel" onClick={onCancel} className="inline-flex items-center gap-2 border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm font-medium px-4 py-2 rounded-md">
          Cancel
        </button>
      </div>
    </div>
  );
};

const AdminTimeSlots = () => {
  const [venues, setVenues] = useState([]);
  const [venueId, setVenueId] = useState("");
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/venues", { withCredentials: true });
        setVenues(data);
        if (data.length) setVenueId(data[0].id);
      } catch {
        toast.error("Could not load venues.");
      }
    })();
  }, []);

  const reload = useCallback(async () => {
    if (!venueId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/venues/${venueId}/time-slots`, { withCredentials: true });
      setSlots(data);
    } catch {
      toast.error("Could not load time slots.");
    } finally {
      setLoading(false);
    }
  }, [venueId]);

  useEffect(() => { reload(); }, [reload]);

  const remove = async (id) => {
    if (!window.confirm("Delete this time slot?")) return;
    try {
      await api.delete(`/admin/time-slots/${id}`, { withCredentials: true });
      toast.success("Deleted.");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed.");
    }
  };

  const toggleActive = async (s) => {
    try {
      await api.patch(`/admin/time-slots/${s.id}`, { time: s.time, price_cad: s.price_cad, active: !s.active }, { withCredentials: true });
      reload();
    } catch {
      toast.error("Update failed.");
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-time-slots">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <Clock className="w-5 h-5 text-spike-blue" /> Time Slots
          </h2>
          <p className="text-sm text-slate-600 mt-1">Set hourly slots and pricing per venue. Inactive slots are hidden from bookers.</p>
        </div>
        <div className="flex gap-2 items-center">
          <select data-testid="ts-venue-select" value={venueId} onChange={(e) => setVenueId(e.target.value)} className={inputCls + " w-auto"}>
            {venues.map((v) => (<option key={v.id} value={v.id}>{v.name}</option>))}
          </select>
          {!creating && !editing && (
            <button type="button" data-testid="ts-add-new" onClick={() => setCreating(true)} className="inline-flex items-center gap-2 bg-spike-blue hover:bg-spike-blue-hover text-white text-sm font-medium px-4 py-2 rounded-md">
              <Plus className="w-4 h-4" /> Add slot
            </button>
          )}
        </div>
      </div>

      {creating && (
        <TimeSlotForm initial={null} venueId={venueId} onCancel={() => setCreating(false)} onSaved={() => { setCreating(false); reload(); }} />
      )}

      {loading ? (
        <div className="py-12 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-spike-blue" /></div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-card">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Time</th>
                <th className="text-left px-4 py-3">Price (CAD)</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {slots.map((s) => (
                <React.Fragment key={s.id}>
                  <tr data-testid={`ts-row-${s.time.replace(":", "")}`} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-medium text-slate-900">{s.time}</td>
                    <td className="px-4 py-3 text-slate-700">${s.price_cad}</td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        data-testid={`ts-toggle-${s.id}`}
                        onClick={() => toggleActive(s)}
                        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                          s.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"
                        }`}
                      >
                        {s.active ? "Active" : "Inactive"}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button type="button" data-testid={`ts-edit-${s.id}`} onClick={() => setEditing(s.id)} className="p-1.5 text-slate-600 hover:text-spike-blue rounded">
                        <Edit3 className="w-4 h-4" />
                      </button>
                      <button type="button" data-testid={`ts-delete-${s.id}`} onClick={() => remove(s.id)} className="p-1.5 text-slate-600 hover:text-red-500 rounded ml-1">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                  {editing === s.id && (
                    <tr><td colSpan={4} className="p-4 bg-slate-50">
                      <TimeSlotForm initial={s} venueId={venueId} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />
                    </td></tr>
                  )}
                </React.Fragment>
              ))}
              {slots.length === 0 && (
                <tr><td colSpan={4} className="px-4 py-12 text-center text-slate-500">No time slots yet. Add one to get started.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminTimeSlots;
